import React from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  ReceiptLongRounded as ReceiptLongRoundedIcon,
  CancelOutlined as CancelOutlinedIcon,
} from "@mui/icons-material";
import StyleVariables from "../../assets/styles/Variable.module.scss";
import { NumericFormat } from "react-number-format";

type Props = {
  list: any[];
  isLoading?: boolean;
  noDataMessage: string;
  onClickBill: (id: string) => void;
  onClickBillCancel: (id: string) => void;
  onClickCancel: (exchange: any) => void;
};

const ExchangeList = (props: Props) => {
  const columns: {
    align: "left" | "right" | "center";
    minWidth: number;
    width: string;
    name: string;
    sticky?: boolean;
    stickyPosition?: 0;
  }[] = [
    {
      align: "center",
      minWidth: 150,
      width: "15%",
      name: "สินค้าทองเก่า",
    },
    {
      align: "center",
      minWidth: 140,
      width: "15%",
      name: "น้ำหนักทองเก่า",
    },
    {
      align: "center",
      minWidth: 150,
      width: "15%",
      name: "สินค้าทองใหม่",
    },
    {
      align: "center",
      minWidth: 140,
      width: "10%",
      name: "จำนวนทองใหม่",
    },
    {
      align: "center",
      minWidth: 145,
      width: "15%",
      name: "น้ำหนักทองใหม่",
    },
    {
      align: "center",
      minWidth: 180,
      width: "15%",
      name: "ราคาสุทธิ",
    },
    {
      align: "center",
      minWidth: 100,
      width: "15%",
      name: "Action",
      sticky: true,
      stickyPosition: 0,
    },
  ];
  return (
    <>
      <TableContainer sx={{ maxHeight: 240, overflow: "auto" }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, columnIndex) => {
                return (
                  <TableCell
                    key={columnIndex}
                    sx={{
                      minWidth: column.minWidth,
                      width: column.width,
                      paddingY: 3,
                      paddingX: 1,
                      bgcolor: StyleVariables["color-background"],
                      ...(column.sticky && {
                        position: "sticky",
                        right: column.stickyPosition,
                        zIndex: 10,
                        "@media (max-width:1148px)": {
                          boxShadow: StyleVariables["shadow-table-sticky"],
                        },
                      }),
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      color={StyleVariables["color-black"]}
                      textAlign={column.align}
                    >
                      {column.name}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {!props.isLoading && props.list?.length ? (
            <TableBody>
              {props.list.map((exchange, index) => {
                return (
                  <TableRow key={index}>
                    {/* Old Product Name */}
                    <TableCell
                      sx={{
                        minWidth: columns[0].minWidth,
                        width: columns[0].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[0].align}
                      >
                        {exchange.oldProductName}
                      </Typography>
                    </TableCell>
                    {/* /.Old Product Name */}

                    {/* Old Total Weight */}
                    <TableCell
                      sx={{
                        minWidth: columns[1].minWidth,
                        width: columns[1].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[1].align}
                      >
                        {exchange.oldTotalWeight}
                      </Typography>
                    </TableCell>
                    {/* /.Old Total Weight */}

                    {/* New Product Name */}
                    <TableCell
                      sx={{
                        minWidth: columns[2].minWidth,
                        width: columns[2].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[2].align}
                      >
                        {exchange.newProductName}
                      </Typography>
                    </TableCell>
                    {/* /.New Product Name */}

                    {/* New Total Quantity */}
                    <TableCell
                      sx={{
                        minWidth: columns[3].minWidth,
                        width: columns[3].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[3].align}
                      >
                        {exchange.newTotalQuantity}
                      </Typography>
                    </TableCell>
                    {/* /.New Total Quantity */}

                    {/* New Total Weight */}
                    <TableCell
                      sx={{
                        minWidth: columns[4].minWidth,
                        width: columns[4].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[4].align}
                      >
                        {exchange.newTotalWeight}
                      </Typography>
                    </TableCell>
                    {/* /.New Total Weight */}

                    {/* Net Pay */}
                    <TableCell
                      sx={{
                        minWidth: columns[5].minWidth,
                        width: columns[5].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color={
                          !exchange.cancelAt
                            ? StyleVariables["color-text"]
                            : StyleVariables["color-primary"]
                        }
                        textAlign={columns[5].align}
                      >
                        <NumericFormat
                          value={exchange.netPay}
                          displayType="text"
                          thousandSeparator
                        />
                      </Typography>
                    </TableCell>
                    {/* /.Net Pay */}

                    {/* Action */}
                    <TableCell
                      sx={{
                        minWidth: columns[6].minWidth,
                        width: columns[6].width,
                        paddingY: 3,
                        paddingX: 1,
                        bgcolor:
                          index % 2 === 0
                            ? StyleVariables["color-white"]
                            : StyleVariables["color-background"],
                        position: "sticky",
                        right: columns[6].stickyPosition,
                        zIndex: 9,
                        "@media (max-width:1148px)": {
                          boxShadow: StyleVariables["shadow-table-sticky"],
                        },
                      }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        gap={3}
                      >
                        {!exchange.cancelAt ? (
                          <>
                            <ReceiptLongRoundedIcon
                              sx={{
                                fontSize: 32,
                                color: !exchange.cancelAt
                                  ? StyleVariables["color-main-icon"]
                                  : StyleVariables["color-menu-icon"],
                                cursor: "pointer",
                              }}
                              onClick={() => props.onClickBill(exchange.id)}
                            />
                            {!exchange.isAccountClosed ? (
                              <CancelOutlinedIcon
                                sx={{
                                  fontSize: 32,
                                  color: StyleVariables["color-menu-icon"],
                                  cursor: "pointer",
                                }}
                                onClick={() => props.onClickCancel(exchange)}
                              />
                            ) : null}
                          </>
                        ) : (
                          <>
                            <ReceiptLongRoundedIcon
                              sx={{
                                fontSize: 32,
                                color: !exchange.cancelAt
                                  ? StyleVariables["color-main-icon"]
                                  : StyleVariables["color-menu-icon"],
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                props.onClickBillCancel(exchange.id)
                              }
                            />
                            <Typography
                              variant="body2"
                              color={StyleVariables["color-primary"]}
                              fontWeight={"medium"}
                              textAlign={"center"}
                            >
                              ยกเลิกแล้ว
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          ) : null}
        </Table>
      </TableContainer>
      {props.isLoading ? (
        <Box
          marginY={4}
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress
            size="40px"
            style={{ color: StyleVariables["color-textfield-icon"] }}
          />
        </Box>
      ) : !props.list?.length ? (
        <Typography
          variant="body2"
          color={StyleVariables["color-placeholder"]}
          fontWeight={"medium"}
          textAlign={"center"}
          marginY={4}
        >
          {props.noDataMessage}
        </Typography>
      ) : null}
    </>
  );
};

export default ExchangeList;
